// src/clock/action.js — clock-in/out action + TOIL accrual (v87.0)
//
// Extracted from index.html in v87.0. Previously lived at lines 6500–6740
// in v86.16. The mutation point for clockEvents — every clock_in, clock_out,
// break_start, break_end goes through doClock.
//
// What's here:
//   - doClock(staffId, action, time) — the action entry point
//   - autoAccrueToil(staffId, clockIn, clockOut, dateStr) — called from
//     doClock on clock_out
//
// Imports:
//   - clockEvents from state.js  → the shared mutable map
//   - loadClockFromDB from state.js → post-error state refresh
//   - captureLocation from location.js → geotag on clock_in / clock_out
//
// Free-variable dependencies:
//   ALL_STAFF, currentStore, currentUser, shifts, persistBreakUpdate,
//   showToast, showConsentModal, showClockSyncError, renderClockScreen,
//   businessDayStr, todayStr, getDayKey, shiftKey, getHolidayForDate,
//   timeMins, DB, dlog. All resolve from window at call time.
//
// Behaviour of this module is governed by two DISTINCT categories of logic.
// Conflating them has historically led to "let me clean this up" refactors
// that quietly broke production. Read this before changing anything.
//
// CATEGORY 1 — DELIBERATE BUSINESS RULES (must NOT be "simplified" away):
//
//   v80.15: businessDayStr 3am–3am boundary. ELC stores in Dubai typically
//           close ~midnight and reopen ~10am; the clock_log "day" aligns
//           with the business day, NOT the calendar day. A clock-out at
//           00:30 on Wednesday morning is part of TUESDAY's shift. This
//           is a product decision (Matt, May 2026), not a defensive
//           workaround. businessDayStr() is the canonical helper.
//
//   v79.8:  TOIL accrues to the SHIFT START date, not today's date.
//           Same business-day rule applied to the autoAccrueToil path —
//           an overnight shift's TOIL goes against the shift's start date
//           so it lines up with the rota record.
//
// CATEGORY 2 — DEFENSIVE LOGIC FROM OBSERVED PILOT BUGS (safer to refactor,
// but the bugs they prevent MUST stay covered by tests):
//
//   v79.17: PIN ownership race guard (in checkClockPin, which stays in
//           index.html for Phase 1 — referenced here because doClock is
//           only entered AFTER the guard passes).
//
//   v80.9:  Stale-state clear on clock_in. Fixed a real bug where a ghost
//           clock-out from the previous day blocked re-clock-in.
//
//   v80.16: Silent-failure surfacing via showClockSyncError. Fixed a real
//           bug where save errors appeared visually-successful but never
//           persisted.
//
// Refactor rule: Category 1 changes are PRODUCT changes (talk to Matt).
// Category 2 changes are safe if better implementations are found.

import { clockEvents, loadClockFromDB, bumpClockGen } from './state.js';
import { captureLocation } from './location.js';
import { forceNextStuckCheck } from './stuck.js';   // v136.8: a clock action re-reads the open sessions on the next draw
import { saveClock } from './outbox.js';   // v124.10: offline write queue (pay-path reliability)

// v128.15 (audit): the persists below are fire-and-forget, and
// persistBreakUpdate is a read-modify-write that re-sends clock_out from its
// read SNAPSHOT. A quick break_end → clock_out tap pair could interleave:
// the break persist's stale read (clock_out=null) landing AFTER the
// clock_out save erased the clock-out and silently reopened the shift
// (wrong hours until the yesterday-banner/manual fix). One device-wide
// chain runs the DB persists strictly in tap order — each read happens
// after the prior write, so snapshots are never stale. The optimistic UI
// is unchanged; only the persistence ORDER is pinned. Failures don't stall
// the chain (each caller keeps its own .catch → showClockSyncError).
var _persistChain = Promise.resolve();
function chainPersist(fn) {
  var next = _persistChain.then(fn, fn);
  _persistChain = next.then(function () {}, function () {});
  return next;
}

// ── v129.46 — the shift-end moment (social layer #5) ────────────────────────
// One earned celebration, at the one point in the day it means something: the
// shift is DONE. Research rule (the confetti case law from the 4 Aug session):
// celebration works layered on real progress and dies from overuse — so this
// fires on clock_out only, never on breaks, and says the one true fact it has
// (the shift length).
//
// GUARD DISCIPLINE: identical to the v126.2 badge moment on clock_in — this is
// ADVISORY, display-only, and can NEVER touch the pay path. It is called after
// the UI update, wrapped in try/catch at the call site, and a throw changes
// nothing about the clock write. prefers-reduced-motion gets the card with no
// confetti — the message is the point, the paper is garnish.
export function celebrateClockOut(name, ev) {
  var mins = null;
  if (ev && ev.clockIn && ev.clockOut) {
    mins = timeMins(ev.clockOut) - timeMins(ev.clockIn);
    if (mins < 0) mins += 24 * 60;                       // overnight wrap
    mins -= (ev.breakMins || 0);
  }
  // Bad data must not be celebrated: a negative or a 16h+ "shift" is a stuck
  // row, and confetti on it would read as the app mocking somebody.
  if (mins == null || mins <= 0 || mins > 16 * 60) return;
  var h = Math.floor(mins / 60), m = mins % 60;
  var dur = h + 'h' + (m ? ' ' + m + 'm' : '');
  var reduced = false;
  try { reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { /* assume motion ok */ }

  var wrap = document.createElement('div');
  wrap.id = 'clockCelebrate';
  wrap.style.cssText = 'position:fixed;inset:0;z-index:9997;display:flex;align-items:center;justify-content:center;pointer-events:none';
  var confetti = '';
  if (!reduced) {
    if (!document.getElementById('clockCelebrateCss')) {
      var st = document.createElement('style');
      st.id = 'clockCelebrateCss';
      st.textContent = '@keyframes ccFall{0%{transform:translateY(-10vh) rotate(0)}100%{transform:translateY(110vh) rotate(720deg)}}' +
        '@keyframes ccPop{0%{transform:scale(.7);opacity:0}60%{transform:scale(1.04)}100%{transform:scale(1);opacity:1}}';
      document.head.appendChild(st);
    }
    var colours = ['#009E3D', '#f59e0b', '#3b82f6', '#ec4899', '#8b5cf6'];
    for (var i = 0; i < 22; i++) {
      confetti += '<span style="position:absolute;top:0;left:' + Math.round((i * 41) % 100) + '%;width:8px;height:12px;border-radius:2px;' +
        'background:' + colours[i % colours.length] + ';animation:ccFall ' + (2.2 + (i % 5) * 0.3) + 's linear ' + ((i % 7) * 0.12) + 's 1 both"></span>';
    }
  }
  wrap.innerHTML = confetti +
    '<div style="background:var(--card);border-radius:18px;padding:22px 26px;text-align:center;box-shadow:0 12px 40px rgba(0,0,0,.25);max-width:300px' +
      (reduced ? '' : ';animation:ccPop .35s ease-out both') + '">' +
      '<div style="font-size:40px">🎉</div>' +
      '<div style="font-weight:900;font-size:16px;margin-top:4px">Shift complete, ' + escapeHtml(String(name || '').split(' ')[0]) + '!</div>' +
      '<div style="font-size:13px;color:var(--tiny);margin-top:4px">' + dur + ' on the floor today. See you next time 👋</div>' +
    '</div>';
  document.body.appendChild(wrap);
  setTimeout(function () { if (wrap.parentNode) wrap.parentNode.removeChild(wrap); }, 3800);
}

// v133.105 (Matt, 12 Oct 2026: "if they clock out, and try and clock back in again (e.g. not a break) within a
// couple of hours we say Looks like you made a mistake"). The smoke check found 8 days in 30 where a second
// clock session began minutes after the first ended: an accidental clock-out and straight back in, which
// the app records as a NEW session (the split-shift rule). Now a clock-in within UNDO_WINDOW_MINS of a
// clock-out on the same business day asks first, and "still on my shift" reopens the same session.
export var UNDO_WINDOW_MINS = 120;
// Pure: minutes since this person's clock-out today, when a clock-in now should ask; otherwise null.
// ev: their clockEvents entry; now: 'HH:MM'; bizDay: businessDayStr().
export function recentClockOutGap(ev, now, bizDay) {
  if (!ev || ev.status !== 'out' || !ev.clockOut || !ev.clockIn) return null;
  if (ev.shiftDate && bizDay && ev.shiftDate !== bizDay) return null;   // a previous day's shift: a new day
  var a = timeMins(ev.clockOut), b = timeMins(now);
  if (a == null || b == null || isNaN(a) || isNaN(b)) return null;
  var gap = b - a;
  if (gap < 0) gap += 24 * 60;   // clocked out before midnight, back after it (same business day)
  return gap <= UNDO_WINDOW_MINS ? gap : null;
}
// The question. Resolves 'undo' or 'new'; anything going wrong resolves 'new' (today's behaviour), so a
// clock-in is never blocked by it.
function askUndoClockOut(name, clockOut, gap) {
  return new Promise(function (resolve) {
    try {
      var wrap = document.createElement('div');
      wrap.id = 'clockUndoAsk';
      wrap.style.cssText = 'position:fixed;inset:0;z-index:9998;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:16px';
      var ago = gap < 1 ? 'just now' : gap === 1 ? '1 minute ago' : gap < 60 ? gap + ' minutes ago'
        : Math.floor(gap / 60) + 'h ' + (gap % 60) + 'm ago';
      wrap.innerHTML =
        '<div style="background:var(--card);border-radius:18px;padding:20px;max-width:360px;width:100%;box-shadow:0 12px 40px rgba(0,0,0,.25)">' +
          '<div style="font-weight:900;font-size:16px;margin-bottom:6px">' + escapeHtml(String(name || '').split(' ')[0]) + ', you clocked out at ' + escapeHtml(clockOut) + ' (' + ago + '). Did you mean to?</div>' +
          '<button type="button" id="clockUndoYes" style="display:block;width:100%;margin-top:12px;padding:12px;border:none;border-radius:12px;background:#009E3D;color:#fff;font-weight:800;font-size:14px;cursor:pointer;font-family:inherit">I\'m still on my shift: undo my clock-out</button>' +
          '<button type="button" id="clockUndoNo" style="display:block;width:100%;margin-top:8px;padding:12px;border:1.5px solid var(--border-strong);border-radius:12px;background:var(--card);color:var(--text);font-weight:700;font-size:14px;cursor:pointer;font-family:inherit">No, I\'m starting a new shift</button>' +
          '<div style="font-size:12px;color:var(--sub);margin-top:10px;text-align:center">Going on a break? Use the Break button next time.</div>' +
        '</div>';
      document.body.appendChild(wrap);
      var done = function (v) { try { wrap.parentNode && wrap.parentNode.removeChild(wrap); } catch (e) { /* gone */ } resolve(v); };
      document.getElementById('clockUndoYes').onclick = function () { done('undo'); };
      document.getElementById('clockUndoNo').onclick = function () { done('new'); };
    } catch (e) { resolve('new'); }
  });
}

export async function doClock(staffId,action,time){
  // v133.105: a clock-in soon after a clock-out asks whether the clock-out was a mistake (see above).
  if (action === 'clock_in') {
    try {
      var _prev = clockEvents[staffId];
      var _gap = recentClockOutGap(_prev, time, businessDayStr());
      if (_gap != null) {
        var _who = (ALL_STAFF.find(function (x) { return x.id === staffId; }) || {}).name;
        if (await askUndoClockOut(_who, _prev.clockOut, _gap) === 'undo') action = 'undo_clock_out';
      }
    } catch (e) { /* the question is advisory: the clock-in goes ahead as before */ }
  }
  // v129.99: invalidate any in-flight loadClockFromDB — its pre-action
  // snapshot must never overwrite the optimistic state we're about to set
  // (the evening rollback race; see state.js clockGen).
  bumpClockGen();
  if (typeof forceNextStuckCheck === 'function') forceNextStuckCheck();   // v136.8: a clock action always reads the open sessions again on the next draw
  if(!clockEvents[staffId])clockEvents[staffId]={status:'out',log:[]};
  var ev=clockEvents[staffId];ev.log=ev.log||[];ev.log.push({action:action,time:time});

  // v79.8 — midnight clock-out fix.
  // Bug: dateStr used to be `todayStr()`, which captures the date AT THE MOMENT
  // OF THE CLOCK ACTION. A clock_out at 00:00 would capture the NEW day, so
  // the row written had `date` = next day, `clock_in` stamped with the new day,
  // and `clock_out` < `clock_in`. The original previous-day row stayed open
  // and the new row was corrupted.
  //
  // v80.9 — clock_in must clear stale state from any previous shift.
  // Bug 580 (5 May): a staff member clocked in at 09:00, accidentally clocked
  // OUT at 14:29 (during a break — see v80.8 break-clockout fix), then
  // clocked back IN at 16:41. The clock_in handler set ev.clockIn='16:41'
  // but did NOT clear ev.clockOut='14:29' or ev.breakStart='13:29'.
  // The persist-payload path then ran tsFor('14:29', postClockIn=true)
  // which saw 14:29 < 16:41 and shifted clock_out to the NEXT DAY
  // (2026-05-06T14:29). Result: 22-hour overnight shift in the DB.
  //
  // Fix: a fresh clock_in is the start of a new shift. Wipe the residual
  // out/break state so persist sees only the new clock_in. shiftDate is
  // already reset above (v79.8); just extend that to include clockOut,
  // breakStart, and breakMins.
  // v128.2 — anchor the shift to the BUSINESS day, not the calendar day.
  // Bug (caught by the staging e2e running at 00:46 Dubai): a clock_in between
  // midnight and 03:00 stamped todayStr() = the NEW calendar day, but every
  // reader (loadClockFromDB, getClockEventForDate, the render default) reads
  // businessDayStr() = the PREVIOUS day. The row was written to a day the UI
  // never reads — the next DB resync wiped the on-floor state and offered
  // "Clock In" again (double clock-in risk, wrong-day pay attribution).
  // businessDayStr() equals todayStr() from 03:00 onward, so daytime shifts
  // are unchanged; only the 00:00–03:00 window is corrected — per the v80.15
  // canonical rule above (a 00:30 shift belongs to the previous business day).
  if (action === 'clock_in') {
    // v129.158 (split shifts step 6) — a SECOND session on one day. If today's
    // session is properly CLOSED (status out with a real clock_out), a fresh
    // clock-in starts session seq+1 instead of reopening-and-wiping the
    // finished one — the one-row world's only option, which lost the first
    // session exactly the way the rota lost its first leg on 28 July. The
    // v80.9 GHOST case (an open/stale session with no real close) keeps the
    // same-row wipe on the SAME seq — that is a correction, not a new session.
    if (ev.status === 'out' && ev.clockOut) {
      ev.seq = (ev.seq || 1) + 1;
    } else {
      ev.seq = ev.seq || 1;
    }
    ev.shiftDate = businessDayStr();
    delete ev.clockOut;
    delete ev.breakStart;
    ev.breakMins = 0;
  }
  var dateStr = ev.shiftDate || businessDayStr();

  if(action==='clock_in'){ev.status='in';ev.clockIn=time;}
  // v133.105: the same session again, as if the clock-out never happened (seq, clockIn and breaks kept);
  // the save below sends clock_out null on the same (staff, date, seq), which reopens the row.
  if(action==='undo_clock_out'){ev.status='in';delete ev.clockOut;}
  // v128.9 — autoAccrueToil is ADVISORY (toasts only since v80.16) but sat
  // unguarded between the state mutation and the render/persist below: any
  // throw inside it (a free global mid-load, malformed holiday data) killed
  // the clock-out's UI update AND its DB save. Same rule as the v126.2
  // achievements guard — an advisory can NEVER block the pay path.
  if(action==='clock_out'){
    ev.status='out';ev.clockOut=time;
    try { autoAccrueToil(staffId,ev.clockIn,time,dateStr); }
    catch (e) { console.warn('[doClock] autoAccrueToil threw (advisory only — clock-out unaffected):', e && e.message); }
  }
  if(action==='break_start'){ev.status='break';ev.breakStart=time;}
  if(action==='break_end'){
    ev.status='in';
    // Accumulate break minutes for the day (persisted to DB below)
    if (ev.breakStart) {
      var mins = timeMins(time) - timeMins(ev.breakStart);
      if (mins < 0) mins += 24*60; // break crossing midnight (unusual but handle it)
      // Clamp to minimum 1 minute — otherwise a start+end within the same
      // minute records as 0 and the break disappears from the UI.
      if (mins < 1) mins = 1;
      ev.breakMins = (ev.breakMins || 0) + mins;
    }
    delete ev.breakStart;
  }
  var s=ALL_STAFF.find(function(x){return x.id===staffId;});
  if (!s) {
    // ALL_STAFF can be empty (offline cold-boot before DB sync) or a stale
    // staffId can leak in from a stale UI element. Don't crash the toast —
    // surface the problem and bail. The persistence path below also guards
    // on `s` so this short-circuits cleanly.
    console.warn('[doClock] staff not found for id:', staffId, '— ALL_STAFF length:', ALL_STAFF.length);
    showToast('Staff not found — please refresh and try again', 'error');
    closeClockModal();
    return;
  }
  var labels={clock_in:'Clocked in',clock_out:'Clocked out',break_start:'Break started',break_end:'Break ended',undo_clock_out:'Back on shift (clock-out undone)'};
  // v79.8: tsFor must handle the midnight wrap. clock_in and break_start
  // happen ON the shift date. clock_out and break_end may happen the
  // FOLLOWING calendar day (overnight shift). Detect by comparing the
  // post-clockIn time to clockIn: if it's lexically earlier, it wrapped.
  // Note: this assumes shifts <24h. >24h open shifts (someone forgot to
  // clock out for a day+) would mis-anchor — documented limit, audit SQL
  // in followups.md surfaces those orphan rows.
  var nextDayStr = (function(){
    var d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() + 1);
    return d.getFullYear() + '-' +
           String(d.getMonth()+1).padStart(2,'0') + '-' +
           String(d.getDate()).padStart(2,'0');
  })();
  var tsFor = function(t, postClockIn) {
    if (!t) return null;
    // postClockIn = the action happens AFTER clock_in lexically. If t < clockIn
    // we know we wrapped past midnight, so stamp with the next day.
    if (postClockIn && ev.clockIn && timeMins(t) < timeMins(ev.clockIn)) {
      return nextDayStr + 'T' + t + ':00';
    }
    return dateStr + 'T' + t + ':00';
  };

  // Update UI immediately so the user sees the clock action complete
  closeClockModal();
  showToast(s.name+' \u2014 '+labels[action]+' '+time,'success');
  renderClockScreen();
  // v126.2 \u2014 \ud83c\udfc6 the badge moment: their newest personal badges float up after a
  // clock-in. Display-only + fully guarded \u2014 achievements can NEVER touch the
  // pay path (a throw here is swallowed; the clock write below is unaffected).
  // v129.46 — the shift-end moment mirrors it on clock_out. Same guard: an
  // advisory can NEVER block the pay path.
  if (action === 'clock_out') {
    try { celebrateClockOut(s.name, ev); } catch (_e) { /* display-only */ }
  }
  if (action === 'clock_in') {
    try { if (typeof Achievements !== 'undefined' && Achievements.onClockIn) Achievements.onClockIn(staffId, s.name); } catch (_e) {}
  }

  // v80.16 — clock save error recovery.
  // Two production reports (Mary @ New Springs Village 10/5, anonymous @ same
  // store 9/5) traced to optimistic-UI + silent DB-write failure: the user
  // taps an action, sees a success toast, but the persist actually failed
  // (network blip, location-capture timeout, etc.) and only console.warn'd.
  // By the next tap the UI has resynced from DB state and is showing the
  // PRIOR action's button — the user taps what they think is the same
  // action, but the system records it as something else (e.g. clock_in
  // instead of clock_out, registering "lateness" for the next morning).
  //
  // Fix: any save failure now (a) shows a visible error toast, (b) reloads
  // clock state from DB so the next render reflects truth, and (c) keeps
  // the original optimistic toast intact (it's not a lie if the user can
  // see the follow-up correction immediately afterwards).
  function showClockSyncError(reason) {
    console.warn('[doClock] save failed — ' + reason);
    showToast('\u26A0 Clock save failed \u2014 refreshing your status. Verify before tapping again.', 'error');
    if (typeof forceNextStuckCheck === 'function') forceNextStuckCheck();   // v136.8: and the stuck check reads the truth too
    if (typeof loadClockFromDB === 'function') {
      loadClockFromDB(dateStr).then(function(){
        if (typeof renderClockScreen === 'function') renderClockScreen();
      }).catch(function(e){ console.warn('[doClock] state refresh after error also failed:', e && e.message); });
    }
  }

  // v131.17 (a store manager, Khawaneej, 2 Sep): the clock row's store is WHERE THE
  // WORK HAPPENED — the till this action is fired from — not the staff
  // record's home store. On a transfer day (or cover shift) s.store is the
  // OLD store until the move lands in staff.store, so her whole 1 Sep shift
  // was booked to Marina Mall and Khawaneej's views showed "never clocked
  // out". currentStore is the till's own store context (same source the
  // clock roster renders from); s.store stays as the fallback.
  function clockStore() {
    if (typeof currentStore !== 'undefined' && currentStore) return currentStore;
    return s ? s.store : null;
  }

  // Helper: find the latest DB row for this staff+date, patch or insert.
  function persistBreakUpdate(breakStartTime, breakMins) {
    return DB.clock.list({ date: dateStr, store: clockStore() }).then(function(rows){   // v131.38: tokenless reads name the store
      if (!Array.isArray(rows)) rows = [];
      var mine = rows.filter(function(r){ return r.staff_id === staffId; });
      if (!mine.length) {
        return DB.clock.save({
          staff_id: staffId,
          store: clockStore(),
          date: dateStr,
          clock_in: tsFor(ev.clockIn) || null,
          clock_out: null,
          break_mins: breakMins || 0,
          // Break may straddle midnight on a late shift — postClockIn=true.
          break_start: breakStartTime ? tsFor(breakStartTime, true) : null,
          seq: ev.seq || 1,   // v129.158 — the live session's row
          notes: '',
        });
      }
      // v129.158 — patch the row of the LIVE session (ev.seq), not blindly the
      // newest id: on a split day the newest row could be the closed morning.
      var target = mine.filter(function(r){ return (r.seq || 1) === (ev.seq || 1); })[0];
      if (!target) { mine.sort(function(a,b){ return (b.id||0) - (a.id||0); }); target = mine[0]; }
      return DB.clock.save({
        staff_id: staffId,
        store: target.store || clockStore(),   // existing row keeps its store; only a storeless row gets stamped
        date: dateStr,
        clock_in: target.clock_in,
        clock_out: target.clock_out,
        break_mins: breakMins || 0,
        break_start: breakStartTime ? tsFor(breakStartTime, true) : null,
        seq: target.seq || 1,
        notes: target.notes || '',
      });
    });
  }

  // Persist. For clock_in/clock_out, capture location (best-effort, won't
  // block the actual save) and include in payload.
  if (action === 'clock_in' || action === 'clock_out' || action === 'undo_clock_out') {   // v133.105: the undo saves like a clock-in
    // Capture location in a guarded way — a location-capture failure
    // (timeout, denied, GPS unavailable) MUST NOT prevent the clock save
    // from happening. Previously a captureLocation() throw would land in
    // the outer catch and DB.clock.save was never called — staff would see
    // a success toast but the DB had nothing.
    var loc = { lat: null, lng: null, accuracy: null, label: null, denied: false, reason: null };
    try {
      loc = await captureLocation();
    } catch (e) {
      console.warn('[doClock] location capture threw:', e && e.message);
      loc = { lat: null, lng: null, accuracy: null, label: null, denied: true, reason: 'error' };
    }
    var payload = {
      staff_id: staffId,
      store: clockStore(),
      date: dateStr,
      clock_in: tsFor(ev.clockIn),
      // clock_out and break_start happen AFTER clock_in by definition.
      // postClockIn=true lets tsFor advance to the next day if the time
      // is lexically earlier (i.e. shift wrapped past midnight).
      clock_out: tsFor(ev.clockOut, true),
      break_mins: ev.breakMins || 0,
      break_start: ev.breakStart ? tsFor(ev.breakStart, true) : null,
      seq: ev.seq || 1,   // v129.158 — which session of the day this row is
      notes: '',
      lat: loc.lat != null ? loc.lat : null,
      lng: loc.lng != null ? loc.lng : null,
      location_accuracy_m: loc.accuracy != null ? loc.accuracy : null,
      location_label: loc.label || null,
      location_denied: loc.denied === true,
    };
    chainPersist(function(){ return saveClock(payload); })
      .then(function(res){
        // v124.10: offline write queue. If the device was offline, saveClock queued the
        // event (idempotent upsert — it replays on reconnect, never duplicates) and
        // resolves { queued:true } instead of throwing. Tell the staffer plainly.
        if (res && res.queued) {
          showToast('✓ Saved offline — will sync automatically when you are back online', 'info');
          return;
        }
        // v136.9: the server may file a clock-in as a NEW session (seq 2) when the session this till named was
        // already finished (src/worker/clock-session.js). Take its seq at once, so this person's clock-out and
        // breaks go to that session even if the reload below fails.
        try {
          var _row = Array.isArray(res) ? res[0] : null;
          if (action === 'clock_in' && _row && !_row.clock_out && Number(_row.seq) >= 1 &&
              clockEvents[staffId] === ev && ev.status === 'in' && ev.clockIn === time) ev.seq = Number(_row.seq);
        } catch (_e) { /* the reload below reconciles */ }
        // Second toast with location info so staff know it was recorded
        if (loc.label) {
          showToast('\uD83D\uDCCD ' + loc.label + ' (\u00B1' + loc.accuracy + 'm)', 'info');
        } else if (loc.lat != null) {
          showToast('\uD83D\uDCCD Location recorded (\u00B1' + loc.accuracy + 'm)', 'info');
        } else if (loc.denied) {
          var msg = loc.reason === 'denied'      ? '\u26A0 Location permission denied \u2014 entry flagged'
                  : loc.reason === 'timeout'     ? '\u26A0 Location timed out \u2014 entry flagged'
                  : loc.reason === 'unavailable' ? '\u26A0 Location unavailable \u2014 entry flagged'
                  : loc.reason === 'error'       ? '\u26A0 Location error \u2014 entry flagged'
                  : '\u26A0 Location unavailable \u2014 entry flagged';
          showToast(msg, 'error');
        }
        // v129.99: reconcile the screen from DB truth after a SUCCESSFUL save
        // (failure already refetches via showClockSyncError). If another
        // action fires while this read is in flight, the clockGen guard
        // discards the stale snapshot — last action always wins.
        if (typeof forceNextStuckCheck === 'function') forceNextStuckCheck();   // v136.8: the saved state, not the pre-save read
        loadClockFromDB().then(function(){
          if (typeof renderClockScreen === 'function') renderClockScreen();
        }).catch(function(){ /* next explicit refresh reconciles */ });
      })
      .catch(function(e){ showClockSyncError('clock save: ' + (e && e.message)); });
  } else if (action === 'break_start') {
    chainPersist(function(){ return persistBreakUpdate(time, ev.breakMins || 0); })
      .catch(function(e){ showClockSyncError('break_start save: ' + (e && e.message)); });
  } else if (action === 'break_end') {
    chainPersist(function(){ return persistBreakUpdate(null, ev.breakMins || 0); })
      .catch(function(e){ showClockSyncError('break_end save: ' + (e && e.message)); });
  }
}
export function autoAccrueToil(staffId,clockIn,clockOut,dateStr){
  if(!clockIn||!clockOut)return;
  // v79.8: dateStr is the SHIFT START DATE (passed in from doClock), not
  // today's date. Without this, an overnight shift's TOIL would look up
  // tomorrow's shift template (empty) and silently skip TOIL accrual.
  // Fallback to the business day only if caller didn't supply (legacy paths).
  if (!dateStr) dateStr = businessDayStr();   // v128.2: was todayStr() — see doClock

  // Convert dateStr → Date for getDayKey. Noon time avoids any DST-related
  // edge cases — we just need the local-date weekday.
  var shiftDate = new Date(dateStr + 'T12:00:00');
  var dayKey = getDayKey(shiftDate);
  var sh=shifts[shiftKey(staffId,dayKey)];
  var holiday=getHolidayForDate(currentStore,dateStr);
  if(holiday){
    var worked=(timeMins(clockOut)-timeMins(clockIn))/60-((sh&&sh.lunch)||60)/60;
    // Overnight clock-out (e.g. 15:00 → 00:00): timeMins(00:00)=0, timeMins(15:00)=900
    // → worked = -15. Add 24h to handle the wrap.
    if (worked < 0) worked += 24;
    var hrs=Math.round(worked*10)/10;
    // v80.16: PH accrual via clock-out removed. The 8h credit for working a
    // public holiday is now computed by /api/balances (count of work shifts
    // on PH dates × 8h). Keeping the toast as informational feedback so the
    // staff member knows their PH work was recognised.
    showToast('+8h TOIL \u2014 worked on '+holiday.name,'success');return;
  }
  if(!sh||!sh.start)return;
  var worked=(timeMins(clockOut)-timeMins(clockIn))/60-(sh.lunch||60)/60;
  if (worked < 0) worked += 24;   // overnight wrap, same as above
  var scheduled=Math.min(8,(timeMins(sh.finish)-timeMins(sh.start))/60-(sh.lunch||60)/60);
  if (scheduled < 0) scheduled += 24;
  var extra=worked-scheduled;
  // v80.16: Overtime auto-accrual removed for now. Rota is the source of truth
  // in the new balance model — automatic clock-based credits would diverge from
  // it. Managers can still credit OT manually via the TOIL modal (saveToil),
  // which writes to balances.toil_balance as an explicit adjustment.
  if(extra>0.25){var hrs=Math.round(extra*10)/10;showToast(hrs+'h overtime \u2014 ask manager to credit if needed','info');}
}
